import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Package, Send, FileText, Building2, Plus, Search, Filter,
  ChevronRight, ChevronDown, MoreHorizontal, Eye, Edit2, Trash2,
  Check, X, Clock, AlertCircle, CheckCircle2, ArrowLeft, Download,
  Users, DollarSign, Calendar, Truck, ClipboardList, ShoppingCart,
  Mail, Phone, ExternalLink, Copy, RefreshCw, ArrowRight, Receipt,
  Loader2, Box, CheckSquare, XSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations
const RFP_STATUS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  responses_received: { label: 'Responses', color: 'bg-amber-100 text-amber-700', icon: Mail },
  awarded: { label: 'Awarded', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-700', icon: X },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XSquare },
};

const QUOTE_STATUS = {
  received: { label: 'Received', color: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-700' },
};

const PO_STATUS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  issued: { label: 'Issued', color: 'bg-blue-100 text-blue-700' },
  confirmed: { label: 'Confirmed', color: 'bg-cyan-100 text-cyan-700' },
  partial: { label: 'Partial', color: 'bg-amber-100 text-amber-700' },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-700' },
  received: { label: 'Received', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function RFPsPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'rfps');
  const [rfps, setRfps] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [showCreateRFP, setShowCreateRFP] = useState(false);
  const [showRFPDetail, setShowRFPDetail] = useState(null);
  const [showAddQuote, setShowAddQuote] = useState(null);
  const [showPODetail, setShowPODetail] = useState(null);
  
  // Form state for new RFP
  const [newRFP, setNewRFP] = useState({
    project_id: projectId || '',
    title: '',
    description: '',
    due_date: '',
    delivery_address: '',
    delivery_requirements: '',
    special_instructions: '',
    selectedClips: [],
    selectedVendors: [],
  });

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rfpsRes, posRes, projectsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/rfps${projectId ? `?project_id=${projectId}` : ''}`),
        axios.get(`${API}/purchase-orders${projectId ? `?project_id=${projectId}` : ''}`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/contacts`),
      ]);
      
      setRfps(rfpsRes.data.rfps || []);
      setPurchaseOrders(posRes.data.purchase_orders || []);
      setProjects(projectsRes.data.projects || []);
      setContacts(contactsRes.data.contacts || contactsRes.data || []);
      
      // If project selected, fetch clips
      if (projectId || newRFP.project_id) {
        const clipsRes = await axios.get(`${API}/clips?project_id=${projectId || newRFP.project_id}`);
        setClips(clipsRes.data.clips || clipsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch clips when project changes
  const fetchProjectClips = async (projId) => {
    if (!projId) return;
    try {
      const res = await axios.get(`${API}/clips?project_id=${projId}`);
      setClips(res.data.clips || res.data || []);
    } catch (error) {
      console.error('Error fetching clips:', error);
    }
  };

  // Filter vendors (contacts with vendor type or company)
  const vendors = useMemo(() => {
    return contacts.filter(c => 
      c.contact_type === 'vendor' || 
      c.contact_type === 'manufacturer' ||
      c.company
    );
  }, [contacts]);

  // Filtered RFPs
  const filteredRFPs = useMemo(() => {
    return rfps.filter(rfp => {
      const matchesSearch = !searchTerm || 
        rfp.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rfp.rfp_number?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || rfp.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [rfps, searchTerm, statusFilter]);

  // Filtered POs
  const filteredPOs = useMemo(() => {
    return purchaseOrders.filter(po => {
      const matchesSearch = !searchTerm || 
        po.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [purchaseOrders, searchTerm, statusFilter]);

  // Create RFP
  const handleCreateRFP = async () => {
    if (!newRFP.project_id || !newRFP.title) {
      toast.error('Please select a project and enter a title');
      return;
    }
    
    if (newRFP.selectedClips.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    try {
      // Build items from selected clips
      const items = newRFP.selectedClips.map(clipId => {
        const clip = clips.find(c => c.id === clipId);
        return {
          clip_id: clipId,
          name: clip?.title || clip?.product_name || 'Unnamed Item',
          description: clip?.notes || '',
          spec_num: clip?.spec_num,
          spec_category: clip?.spec_category,
          quantity: clip?.quantity || 1,
          unit: clip?.unit || 'Each',
          room: clip?.room,
          manufacturer: clip?.manufacturer,
          model_number: clip?.model_number,
          finish: clip?.finish,
          image_url: clip?.primary_image,
        };
      });

      const res = await axios.post(`${API}/rfps`, {
        project_id: newRFP.project_id,
        title: newRFP.title,
        description: newRFP.description,
        items,
        due_date: newRFP.due_date || null,
        delivery_address: newRFP.delivery_address,
        delivery_requirements: newRFP.delivery_requirements,
        special_instructions: newRFP.special_instructions,
        vendor_ids: newRFP.selectedVendors,
      });

      toast.success(`RFP ${res.data.rfp_number} created`);
      setShowCreateRFP(false);
      setNewRFP({
        project_id: projectId || '',
        title: '',
        description: '',
        due_date: '',
        delivery_address: '',
        delivery_requirements: '',
        special_instructions: '',
        selectedClips: [],
        selectedVendors: [],
      });
      fetchData();
    } catch (error) {
      console.error('Error creating RFP:', error);
      toast.error('Failed to create RFP');
    }
  };

  // Send RFP
  const handleSendRFP = async (rfpId) => {
    try {
      await axios.post(`${API}/rfps/${rfpId}/send`);
      toast.success('RFP sent to vendors');
      fetchData();
    } catch (error) {
      toast.error('Failed to send RFP');
    }
  };

  // Accept quote
  const handleAcceptQuote = async (rfpId, quoteId) => {
    try {
      const res = await axios.post(`${API}/rfps/${rfpId}/quotes/${quoteId}/accept?create_po=true`);
      toast.success(`Quote accepted! PO ${res.data.po_number} created`);
      setShowRFPDetail(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to accept quote');
    }
  };

  // Issue PO
  const handleIssuePO = async (poId) => {
    try {
      await axios.post(`${API}/purchase-orders/${poId}/issue`);
      toast.success('Purchase order issued');
      fetchData();
    } catch (error) {
      toast.error('Failed to issue PO');
    }
  };

  // Delete RFP
  const handleDeleteRFP = async (rfpId) => {
    if (!window.confirm('Are you sure you want to delete this RFP?')) return;
    try {
      await axios.delete(`${API}/rfps/${rfpId}`);
      toast.success('RFP deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete RFP');
    }
  };

  // Summary stats
  const stats = useMemo(() => ({
    totalRFPs: rfps.length,
    draftRFPs: rfps.filter(r => r.status === 'draft').length,
    awaitingResponse: rfps.filter(r => r.status === 'sent').length,
    totalPOs: purchaseOrders.length,
    openPOs: purchaseOrders.filter(p => !['received', 'cancelled'].includes(p.status)).length,
    totalPOValue: purchaseOrders.reduce((sum, po) => sum + (po.total || 0), 0),
  }), [rfps, purchaseOrders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#70170F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <span>Financials</span>
                  <ChevronRight className="w-4 h-4" />
                  <span>RFPs & Procurement</span>
                </div>
                <div className="flex items-center gap-2">
                  <HelpIcon elementId="bid-request" position="inline" />
                  <h1 className="text-2xl font-heading font-bold text-[#1A1918]">
                    RFPs & Purchase Orders
                  </h1>
                  <BlueprintIndicator sectionId="rfps-overview" tooltip="Learn about RFPs & POs" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowCreateRFP(true)} className="bg-[#70170F] hover:bg-[#8B1D12]">
                <Plus className="w-4 h-4 mr-2" />
                New RFP
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#70170F]" />
                <div>
                  <p className="text-2xl font-bold">{stats.totalRFPs}</p>
                  <p className="text-xs text-muted-foreground">Total RFPs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.awaitingResponse}</p>
                  <p className="text-xs text-muted-foreground">Awaiting Response</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.totalPOs}</p>
                  <p className="text-xs text-muted-foreground">Purchase Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.openPOs}</p>
                  <p className="text-xs text-muted-foreground">Open POs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="col-span-2">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalPOValue)}</p>
                  <p className="text-xs text-muted-foreground">Total PO Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="rfps" className="gap-2">
                <FileText className="w-4 h-4" />
                RFPs
              </TabsTrigger>
              <TabsTrigger value="purchase-orders" className="gap-2">
                <ShoppingCart className="w-4 h-4" />
                Purchase Orders
              </TabsTrigger>
              <TabsTrigger value="materials" className="gap-2">
                <Package className="w-4 h-4" />
                Materials List
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {activeTab === 'rfps' ? (
                    <>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="responses_received">Responses</SelectItem>
                      <SelectItem value="awarded">Awarded</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="issued">Issued</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="received">Received</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* RFPs Tab */}
          <TabsContent value="rfps">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>RFP #</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Vendors</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRFPs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No RFPs found. Create your first RFP to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRFPs.map((rfp) => {
                        const project = projects.find(p => p.id === rfp.project_id);
                        const statusConfig = RFP_STATUS[rfp.status] || RFP_STATUS.draft;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <TableRow 
                            key={rfp.id} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => setShowRFPDetail(rfp)}
                          >
                            <TableCell className="font-mono font-medium">
                              {rfp.rfp_number}
                            </TableCell>
                            <TableCell className="font-medium">{rfp.title}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {project?.name || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{rfp.items?.length || 0} items</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{rfp.vendors?.length || 0} vendors</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(rfp.due_date)}
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowRFPDetail(rfp); }}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  {rfp.status === 'draft' && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleSendRFP(rfp.id); }}>
                                      <Send className="w-4 h-4 mr-2" />
                                      Send to Vendors
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleDeleteRFP(rfp.id); }}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Purchase Orders Tab */}
          <TabsContent value="purchase-orders">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO #</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPOs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No purchase orders found. Accept an RFP quote to create a PO.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPOs.map((po) => {
                        const project = projects.find(p => p.id === po.project_id);
                        const statusConfig = PO_STATUS[po.status] || PO_STATUS.draft;
                        
                        return (
                          <TableRow 
                            key={po.id} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => setShowPODetail(po)}
                          >
                            <TableCell className="font-mono font-medium">
                              {po.po_number}
                            </TableCell>
                            <TableCell className="font-medium">{po.vendor_name}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {project?.name || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{po.items?.length || 0} items</Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(po.total)}
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(po.issued_at)}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowPODetail(po); }}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  {po.status === 'draft' && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleIssuePO(po.id); }}>
                                      <Send className="w-4 h-4 mr-2" />
                                      Issue PO
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Materials List Tab */}
          <TabsContent value="materials">
            <MaterialsListView projectId={projectId} projects={projects} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create RFP Dialog */}
      <Dialog open={showCreateRFP} onOpenChange={setShowCreateRFP}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New RFP</DialogTitle>
            <DialogDescription>
              Request for Proposal to send to vendors for pricing
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Project & Title */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project *</Label>
                <Select 
                  value={newRFP.project_id} 
                  onValueChange={(v) => {
                    setNewRFP({...newRFP, project_id: v, selectedClips: []});
                    fetchProjectClips(v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>RFP Title *</Label>
                <Input 
                  value={newRFP.title}
                  onChange={(e) => setNewRFP({...newRFP, title: e.target.value})}
                  placeholder="e.g., Kitchen Appliances Quote Request"
                />
              </div>
            </div>

            {/* Due Date & Description */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Response Due Date</Label>
                <Input 
                  type="date"
                  value={newRFP.due_date}
                  onChange={(e) => setNewRFP({...newRFP, due_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={newRFP.description}
                  onChange={(e) => setNewRFP({...newRFP, description: e.target.value})}
                  placeholder="Brief description of this RFP"
                />
              </div>
            </div>

            {/* Select Items */}
            <div className="space-y-2">
              <Label>Select Items to Quote *</Label>
              <Card className="max-h-60 overflow-y-auto">
                <CardContent className="p-2">
                  {clips.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {newRFP.project_id ? 'No clips found for this project' : 'Select a project first'}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {clips.map(clip => (
                        <div 
                          key={clip.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            newRFP.selectedClips.includes(clip.id) 
                              ? 'bg-[#70170F]/10 border border-[#70170F]/30' 
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => {
                            const selected = newRFP.selectedClips.includes(clip.id)
                              ? newRFP.selectedClips.filter(id => id !== clip.id)
                              : [...newRFP.selectedClips, clip.id];
                            setNewRFP({...newRFP, selectedClips: selected});
                          }}
                        >
                          <Checkbox checked={newRFP.selectedClips.includes(clip.id)} />
                          {clip.primary_image && (
                            <img 
                              src={clip.primary_image} 
                              alt="" 
                              className="w-10 h-10 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {clip.title || clip.product_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {clip.manufacturer} • {clip.room || 'No room'} • Qty: {clip.quantity || 1}
                            </p>
                          </div>
                          {clip.spec_num && (
                            <Badge variant="outline" className="text-xs">{clip.spec_num}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground">
                {newRFP.selectedClips.length} items selected
              </p>
            </div>

            {/* Select Vendors */}
            <div className="space-y-2">
              <Label>Select Vendors to Send RFP</Label>
              <Card className="max-h-40 overflow-y-auto">
                <CardContent className="p-2">
                  {vendors.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No vendor contacts found. Add vendors in Contacts.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {vendors.map(vendor => (
                        <div 
                          key={vendor.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            newRFP.selectedVendors.includes(vendor.id) 
                              ? 'bg-blue-50 border border-blue-200' 
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => {
                            const selected = newRFP.selectedVendors.includes(vendor.id)
                              ? newRFP.selectedVendors.filter(id => id !== vendor.id)
                              : [...newRFP.selectedVendors, vendor.id];
                            setNewRFP({...newRFP, selectedVendors: selected});
                          }}
                        >
                          <Checkbox checked={newRFP.selectedVendors.includes(vendor.id)} />
                          <Building2 className="w-8 h-8 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{vendor.company || vendor.name}</p>
                            <p className="text-xs text-muted-foreground">{vendor.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Delivery Info */}
            <div className="space-y-2">
              <Label>Delivery Address</Label>
              <Textarea 
                value={newRFP.delivery_address}
                onChange={(e) => setNewRFP({...newRFP, delivery_address: e.target.value})}
                placeholder="Delivery address for items"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Special Instructions</Label>
              <Textarea 
                value={newRFP.special_instructions}
                onChange={(e) => setNewRFP({...newRFP, special_instructions: e.target.value})}
                placeholder="Any special requirements or notes for vendors"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRFP(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateRFP} className="bg-[#70170F] hover:bg-[#8B1D12]">
              Create RFP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RFP Detail Dialog */}
      {showRFPDetail && (
        <RFPDetailDialog 
          rfp={showRFPDetail} 
          projects={projects}
          onClose={() => setShowRFPDetail(null)}
          onSend={() => handleSendRFP(showRFPDetail.id)}
          onAcceptQuote={(quoteId) => handleAcceptQuote(showRFPDetail.id, quoteId)}
          onRefresh={fetchData}
        />
      )}

      {/* PO Detail Dialog */}
      {showPODetail && (
        <PODetailDialog 
          po={showPODetail}
          projects={projects}
          onClose={() => setShowPODetail(null)}
          onIssue={() => handleIssuePO(showPODetail.id)}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
}

// RFP Detail Dialog Component
function RFPDetailDialog({ rfp, projects, onClose, onSend, onAcceptQuote, onRefresh }) {
  const project = projects.find(p => p.id === rfp.project_id);
  const statusConfig = RFP_STATUS[rfp.status] || RFP_STATUS.draft;
  const StatusIcon = statusConfig.icon;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                {rfp.rfp_number}
                <Badge className={statusConfig.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
              </DialogTitle>
              <DialogDescription>{rfp.title}</DialogDescription>
            </div>
            {rfp.status === 'draft' && (
              <Button onClick={onSend} className="bg-[#70170F] hover:bg-[#8B1D12]">
                <Send className="w-4 h-4 mr-2" />
                Send to Vendors
              </Button>
            )}
          </div>
        </DialogHeader>

        <Tabs defaultValue="items">
          <TabsList>
            <TabsTrigger value="items">Items ({rfp.items?.length || 0})</TabsTrigger>
            <TabsTrigger value="vendors">Vendors ({rfp.vendors?.length || 0})</TabsTrigger>
            <TabsTrigger value="quotes">Quotes ({rfp.quotes?.length || 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="items" className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Spec #</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Manufacturer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rfp.items?.map((item, idx) => (
                  <TableRow key={item.id || idx}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {item.image_url && (
                          <img src={item.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                        )}
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.model_number && (
                            <p className="text-xs text-muted-foreground">Model: {item.model_number}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.spec_num && <Badge variant="outline">{item.spec_num}</Badge>}
                    </TableCell>
                    <TableCell>{item.room || '-'}</TableCell>
                    <TableCell>{item.quantity} {item.unit}</TableCell>
                    <TableCell>{item.manufacturer || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="vendors" className="mt-4">
            <div className="space-y-2">
              {rfp.vendors?.map((vendor, idx) => (
                <Card key={vendor.vendor_id || idx}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-8 h-8 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{vendor.vendor_name}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {vendor.vendor_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {vendor.vendor_email}
                            </span>
                          )}
                          {vendor.vendor_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {vendor.vendor_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        vendor.status === 'responded' ? 'bg-emerald-100 text-emerald-700' :
                        vendor.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {vendor.status === 'responded' ? 'Responded' :
                         vendor.status === 'sent' ? 'Sent' : 'Pending'}
                      </Badge>
                      {vendor.sent_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Sent: {formatDate(vendor.sent_at)}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {(!rfp.vendors || rfp.vendors.length === 0) && (
                <p className="text-center text-muted-foreground py-4">No vendors added yet</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="quotes" className="mt-4">
            <div className="space-y-4">
              {rfp.quotes?.map((quote, idx) => {
                const quoteStatus = QUOTE_STATUS[quote.status] || QUOTE_STATUS.received;
                return (
                  <Card key={quote.id || idx}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{quote.vendor_name}</CardTitle>
                          <CardDescription>
                            Quote #{quote.quote_number || 'N/A'} • {formatDate(quote.quote_date)}
                          </CardDescription>
                        </div>
                        <div className="text-right">
                          <Badge className={quoteStatus.color}>{quoteStatus.label}</Badge>
                          <p className="text-2xl font-bold mt-1">{formatCurrency(quote.total)}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-muted-foreground">Subtotal</p>
                          <p className="font-medium">{formatCurrency(quote.subtotal)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Tax</p>
                          <p className="font-medium">{formatCurrency(quote.tax)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Shipping</p>
                          <p className="font-medium">{formatCurrency(quote.shipping)}</p>
                        </div>
                      </div>
                      {quote.status === 'received' && rfp.status !== 'awarded' && (
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm">
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => onAcceptQuote(quote.id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Accept & Create PO
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {(!rfp.quotes || rfp.quotes.length === 0) && (
                <p className="text-center text-muted-foreground py-4">No quotes received yet</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// PO Detail Dialog Component
function PODetailDialog({ po, projects, onClose, onIssue, onRefresh }) {
  const project = projects.find(p => p.id === po.project_id);
  const statusConfig = PO_STATUS[po.status] || PO_STATUS.draft;
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showSubstitutionDialog, setShowSubstitutionDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [receiveData, setReceiveData] = useState({});
  const [substitution, setSubstitution] = useState({ original_item_id: '', new_name: '', new_quantity: 0, new_unit_price: 0, reason: '', create_change_order: false });

  const handleReceiveItems = async () => {
    try {
      const itemsToReceive = Object.entries(receiveData)
        .filter(([_, data]) => data.quantity > 0)
        .map(([itemId, data]) => ({
          item_id: itemId,
          quantity_received: data.quantity,
          notes: data.notes || ''
        }));

      if (itemsToReceive.length === 0) {
        toast.error('Enter quantities to receive');
        return;
      }

      await axios.post(`${API}/purchase-orders/${po.id}/receive`, itemsToReceive);
      toast.success('Items received successfully');
      setShowReceiveDialog(false);
      setReceiveData({});
      onRefresh();
    } catch (error) {
      toast.error('Failed to receive items');
    }
  };

  const handleSubstitution = async () => {
    try {
      const response = await axios.post(`${API}/purchase-orders/${po.id}/substitute`, {
        original_item_id: substitution.original_item_id,
        new_item: {
          name: substitution.new_name,
          quantity_ordered: substitution.new_quantity,
          unit_price: substitution.new_unit_price,
          unit: selectedItem?.unit || 'Each'
        },
        reason: substitution.reason,
        create_change_order: substitution.create_change_order
      });
      
      toast.success(response.data.change_order_id 
        ? `Substitution recorded and Change Order created`
        : 'Substitution recorded');
      setShowSubstitutionDialog(false);
      setSubstitution({ original_item_id: '', new_name: '', new_quantity: 0, new_unit_price: 0, reason: '', create_change_order: false });
      onRefresh();
    } catch (error) {
      toast.error('Failed to process substitution');
    }
  };

  const totalOrdered = po.items?.reduce((sum, i) => sum + (i.quantity_ordered || 0), 0) || 0;
  const totalReceived = po.items?.reduce((sum, i) => sum + (i.quantity_received || 0), 0) || 0;
  const receiveProgress = totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                {po.po_number}
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              </DialogTitle>
              <DialogDescription>
                {po.vendor_name} • {project?.name || 'Unknown Project'}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {po.status === 'draft' && (
                <Button onClick={onIssue} className="bg-[#70170F] hover:bg-[#8B1D12]">
                  <Send className="w-4 h-4 mr-2" />
                  Issue PO
                </Button>
              )}
              {['issued', 'confirmed', 'partial', 'shipped'].includes(po.status) && (
                <Button onClick={() => setShowReceiveDialog(true)} variant="outline">
                  <Truck className="w-4 h-4 mr-2" />
                  Receive Items
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Delivery Progress */}
          {po.status !== 'draft' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>Delivery Progress</span>
                  <span className="text-muted-foreground">{receiveProgress}%</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Progress value={receiveProgress} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  {totalReceived} of {totalOrdered} items received
                </p>
              </CardContent>
            </Card>
          )}

          {/* Vendor Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Vendor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Building2 className="w-10 h-10 text-muted-foreground" />
                <div>
                  <p className="font-medium">{po.vendor_name}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {po.vendor_email && <span>{po.vendor_email}</span>}
                    {po.vendor_phone && <span>{po.vendor_phone}</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <div>
            <h3 className="font-medium mb-2">Items ({po.items?.length || 0})</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Unit Price</TableHead>
                  <TableHead>Extended</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {po.items?.map((item, idx) => (
                  <TableRow key={item.id || idx}>
                    <TableCell className="font-medium">
                      {item.name}
                      {item.substituted_for && (
                        <Badge variant="outline" className="ml-2 text-xs">Substitution</Badge>
                      )}
                    </TableCell>
                    <TableCell>{item.quantity_ordered} {item.unit}</TableCell>
                    <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                    <TableCell>{formatCurrency(item.extended_price)}</TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          (item.quantity_received || 0) >= item.quantity_ordered 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                            : (item.quantity_received || 0) > 0 
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : ''
                        }`}
                      >
                        {item.quantity_received || 0}/{item.quantity_ordered}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {['issued', 'confirmed', 'partial', 'shipped'].includes(po.status) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedItem(item);
                              setSubstitution({
                                original_item_id: item.id,
                                new_name: '',
                                new_quantity: item.quantity_ordered,
                                new_unit_price: item.unit_price,
                                reason: '',
                                create_change_order: false
                              });
                              setShowSubstitutionDialog(true);
                            }}>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Record Substitution
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <Card>
            <CardContent className="pt-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(po.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatCurrency(po.tax)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{formatCurrency(po.shipping)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(po.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Receive Items Dialog */}
        <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Receive Items</DialogTitle>
              <DialogDescription>Enter quantities received for each item</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
              {po.items?.filter(i => (i.quantity_received || 0) < i.quantity_ordered).map((item) => (
                <div key={item.id} className="flex items-center gap-4 p-3 border rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.quantity_received || 0} of {item.quantity_ordered} received
                    </p>
                  </div>
                  <div className="w-24">
                    <Input
                      type="number"
                      placeholder="Qty"
                      min={0}
                      max={item.quantity_ordered - (item.quantity_received || 0)}
                      value={receiveData[item.id]?.quantity || ''}
                      onChange={(e) => setReceiveData(prev => ({
                        ...prev,
                        [item.id]: { ...prev[item.id], quantity: parseInt(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>Cancel</Button>
              <Button onClick={handleReceiveItems} className="bg-[#70170F] hover:bg-[#8B1D12]">
                <Check className="w-4 h-4 mr-2" />
                Confirm Receipt
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Substitution Dialog */}
        <Dialog open={showSubstitutionDialog} onOpenChange={setShowSubstitutionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Substitution</DialogTitle>
              <DialogDescription>
                Original: {selectedItem?.name} ({selectedItem?.quantity_ordered} {selectedItem?.unit})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Substitute Item Name</Label>
                <Input
                  placeholder="New item name"
                  value={substitution.new_name}
                  onChange={(e) => setSubstitution(prev => ({ ...prev, new_name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={substitution.new_quantity}
                    onChange={(e) => setSubstitution(prev => ({ ...prev, new_quantity: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={substitution.new_unit_price}
                    onChange={(e) => setSubstitution(prev => ({ ...prev, new_unit_price: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <div>
                <Label>Reason for Substitution</Label>
                <Textarea
                  placeholder="Why is this item being substituted?"
                  value={substitution.reason}
                  onChange={(e) => setSubstitution(prev => ({ ...prev, reason: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2 p-3 border rounded-lg bg-amber-50">
                <input
                  type="checkbox"
                  id="create-co"
                  checked={substitution.create_change_order}
                  onChange={(e) => setSubstitution(prev => ({ ...prev, create_change_order: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="create-co" className="text-sm cursor-pointer">
                  Create Change Order for price difference
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubstitutionDialog(false)}>Cancel</Button>
              <Button onClick={handleSubstitution} className="bg-[#70170F] hover:bg-[#8B1D12]">
                <RefreshCw className="w-4 h-4 mr-2" />
                Record Substitution
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// Materials List View Component
function MaterialsListView({ projectId, projects }) {
  const [materials, setMaterials] = useState({ groups: [], summary: {} });
  const [groupBy, setGroupBy] = useState('room');
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedProject) {
      fetchMaterials();
    }
  }, [selectedProject, groupBy]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/projects/${selectedProject}/materials-list?group_by=${groupBy}`);
      setMaterials(res.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
      toast.error('Failed to load materials list');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-4">
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="room">By Room</SelectItem>
            <SelectItem value="vendor">By Vendor</SelectItem>
            <SelectItem value="category">By Category</SelectItem>
            <SelectItem value="status">By Status</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={fetchMaterials} disabled={!selectedProject}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary */}
      {materials.summary && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{materials.summary.total_items || 0}</p>
              <p className="text-xs text-muted-foreground">Total Items</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{formatCurrency(materials.summary.total_value || 0)}</p>
              <p className="text-xs text-muted-foreground">Total Value</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{materials.summary.items_ordered || 0}</p>
              <p className="text-xs text-muted-foreground">Ordered</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{materials.summary.items_received || 0}</p>
              <p className="text-xs text-muted-foreground">Received</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{materials.summary.items_pending || 0}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Materials Groups */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-[#70170F]" />
        </div>
      ) : !selectedProject ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Select a project to view materials list
          </CardContent>
        </Card>
      ) : materials.groups?.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No materials found for this project
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {materials.groups.map((group, idx) => (
            <Card key={idx}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{group.name}</CardTitle>
                  <div className="text-right">
                    <Badge variant="secondary">{group.total_items} items</Badge>
                    <p className="text-sm font-medium mt-1">{formatCurrency(group.total_value)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Spec #</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>PO #</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.items.map((item, itemIdx) => (
                      <TableRow key={item.id || itemIdx}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {item.image_url && (
                              <img src={item.image_url} alt="" className="w-8 h-8 object-cover rounded" />
                            )}
                            <div>
                              <p className="font-medium text-sm">{item.name}</p>
                              <p className="text-xs text-muted-foreground">{item.vendor}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.spec_num && <Badge variant="outline" className="text-xs">{item.spec_num}</Badge>}
                        </TableCell>
                        <TableCell>{item.quantity} {item.unit}</TableCell>
                        <TableCell>{formatCurrency(item.extended_price)}</TableCell>
                        <TableCell>
                          <Badge className={
                            item.procurement_status === 'received' ? 'bg-emerald-100 text-emerald-700' :
                            item.procurement_status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                            item.po_number ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {item.procurement_status === 'received' ? 'Received' :
                             item.procurement_status === 'shipped' ? 'Shipped' :
                             item.po_number ? 'Ordered' : 'Not Ordered'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {item.po_number || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
